// src/App.js
import React, { useEffect } from 'react';
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme/theme';
import DashboardLayout from './components/layout/DashboardLayout';
import OrdersPage from './pages/OrdersPage';
import RidersPortal from './components/RidersPortal';
import Login from './components/Login';
import RoleBasedRoute from './components/RoleBasedRoute';
import UserAdminPage from './pages/UserAdminPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import Companies from './pages/Companies';
import CustomersPage from './pages/CustomersPage';
import WhatsNewPage from './pages/WhatsNewPage';
import { trackPageView } from './utils/rybbit';
import { startSessionWatch } from './utils/sessionAuth';

const DashboardShell = ({ children }) => <DashboardLayout>{children}</DashboardLayout>;

const App = () => {
  const location = useLocation();
  const userRole = localStorage.getItem('userRole');

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  useEffect(() => {
    startSessionWatch();
  }, []);

  const getDefaultRoute = () => {
    if (!userRole) return '/login';
    return userRole === 'rider' ? '/riders-portal' : '/';
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        <Route path="/login" element={<Login />} />

        <Route
          path="/whats-new"
          element={
            !userRole ? (
              <Navigate to="/login" />
            ) : (
              <WhatsNewPage />
            )
          }
        />

        <Route
          path="/"
          element={
            !userRole ? (
              <Navigate to="/login" />
            ) : userRole === 'rider' ? (
              <Navigate to="/riders-portal" replace />
            ) : (
              <DashboardShell>
                <RoleBasedRoute allowedRoles={['clerk', 'cus-admin', 'super-admin']}>
                  <AnalyticsDashboard />
                </RoleBasedRoute>
              </DashboardShell>
            )
          }
        />

        <Route
          path="/orders"
          element={
            <DashboardShell>
              <RoleBasedRoute allowedRoles={['clerk', 'cus-admin', 'super-admin']}>
                <OrdersPage />
              </RoleBasedRoute>
            </DashboardShell>
          }
        />

        <Route
          path="/riders-portal"
          element={
            <DashboardShell>
              <RoleBasedRoute allowedRoles={['rider']}>
                <RidersPortal />
              </RoleBasedRoute>
            </DashboardShell>
          }
        />

        <Route
          path="/analytics"
          element={
            <DashboardShell>
              <RoleBasedRoute allowedRoles={['clerk', 'cus-admin']}>
                <AnalyticsDashboard />
              </RoleBasedRoute>
            </DashboardShell>
          }
        />

        <Route
          path="/admin/users"
          element={
            <DashboardShell>
              <RoleBasedRoute allowedRoles={['cus-admin', 'super-admin']}>
                <UserAdminPage />
              </RoleBasedRoute>
            </DashboardShell>
          }
        />

        <Route
          path="/companies"
          element={
            <DashboardShell>
              <RoleBasedRoute allowedRoles={['super-admin']}>
                <Companies />
              </RoleBasedRoute>
            </DashboardShell>
          }
        />

        <Route
          path="/customers"
          element={
            <DashboardShell>
              <RoleBasedRoute allowedRoles={['clerk', 'cus-admin', 'super-admin']}>
                <CustomersPage />
              </RoleBasedRoute>
            </DashboardShell>
          }
        />

        <Route path="*" element={<Navigate to={getDefaultRoute()} />} />
      </Routes>
    </ThemeProvider>
  );
};

export default App;
